import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Vorher/Nachher-Vergleich: Ein Regler blendet zwischen zwei gleich großen Bildern.
 * - Maus, Touch (senkrechtes Scrollen bleibt möglich) und Tastatur (Pfeiltasten, Pos1/Ende)
 * - Beim ersten Sichtbarwerden fährt der Regler einmal kurz hin und her, damit man den Übergang sieht
 *   (entfällt bei „Bewegung reduzieren“ und sobald jemand selbst bedient)
 * - Die Knöpfe „Vorher“/„Nachher“ schieben den Regler animiert zum Rand
 * Position = Anteil in Prozent, der vom Vorher-Bild von links sichtbar ist.
 * Props: before/after (Bilder), beforeAlt/afterAlt (Alt-Texte), ratio = [Breite, Höhe] der Bühne (Standard [9, 16]),
 * objectPosition (CSS), beforeLabel/afterLabel (Beschriftungen), caption (Hinweistext darunter).
 * width/height = Pixelmaße der beiden (gleich großen) Bilddateien; die Darstellung bestimmt allein das CSS (ratio).
 */
const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

export default function BeforeAfter({
  before,
  after,
  beforeAlt,
  afterAlt,
  width,
  height,
  ratio = [9, 16],
  objectPosition = "center",
  beforeLabel = "Vorher",
  afterLabel = "Nachher",
  caption,
}) {
  const [pos, setPos] = useState(50);
  const [hint, setHint] = useState(true);
  const stageRef = useRef(null);
  const posRef = useRef(50);
  const rafRef = useRef(0);
  const touchedRef = useRef(false);
  const draggingRef = useRef(false);

  const reduced = () =>
    typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const apply = useCallback((v) => {
    const c = Math.min(100, Math.max(0, v));
    posRef.current = c;
    setPos(c);
  }, []);

  const stop = useCallback(() => cancelAnimationFrame(rafRef.current), []);

  // Sanfte Fahrt zu einer Position; löst auf, wenn sie ankommt oder abgebrochen wird
  const animateTo = useCallback(
    (target, ms = 700) =>
      new Promise((resolve) => {
        stop();
        if (reduced()) { apply(target); resolve(true); return; }
        const from = posRef.current;
        const t0 = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - t0) / ms);
          apply(from + (target - from) * ease(t));
          if (t < 1) rafRef.current = requestAnimationFrame(step);
          else resolve(true);
        };
        rafRef.current = requestAnimationFrame(step);
      }),
    [apply, stop]
  );

  // Kurzer Hinweis-Durchlauf, sobald der Vergleich im Bild ist
  useEffect(() => {
    const el = stageRef.current;
    if (!el || reduced() || !("IntersectionObserver" in window)) return undefined;
    let cancelled = false;
    const io = new IntersectionObserver(
      async (entries) => {
        if (!entries.some((e) => e.isIntersecting) || touchedRef.current) return;
        io.disconnect();
        await new Promise((r) => setTimeout(r, 400));
        for (const [target, ms] of [[88, 1000], [12, 1700], [50, 1000]]) {
          if (cancelled || touchedRef.current) return;
          await animateTo(target, ms);
        }
      },
      { threshold: 0.6 }
    );
    io.observe(el);
    return () => { cancelled = true; io.disconnect(); stop(); };
  }, [animateTo, stop]);

  const fromPointer = (e) => {
    const r = stageRef.current.getBoundingClientRect();
    return ((e.clientX - r.left) / r.width) * 100;
  };
  const engage = () => { touchedRef.current = true; setHint(false); stop(); };

  const onPointerDown = (e) => {
    engage();
    draggingRef.current = true;
    stageRef.current.setPointerCapture?.(e.pointerId);
    apply(fromPointer(e));
  };
  const onPointerMove = (e) => { if (draggingRef.current) apply(fromPointer(e)); };
  const onPointerUp = (e) => {
    draggingRef.current = false;
    stageRef.current.releasePointerCapture?.(e.pointerId);
  };

  const go = (target) => { engage(); animateTo(target, 800); };
  const rounded = Math.round(pos);

  return (
    <figure className="ff-ba">
      <div
        ref={stageRef}
        className="ff-ba__stage"
        style={{ "--pos": `${pos}%`, aspectRatio: `${ratio[0]} / ${ratio[1]}`, "--ba-w": `calc(78vh * ${ratio[0]} / ${ratio[1]})` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img className="ff-ba__img" src={after} width={width} height={height} alt={afterAlt} style={{ objectPosition }} draggable={false} loading="lazy" decoding="async" />
        <img className="ff-ba__img ff-ba__img--before" src={before} width={width} height={height} alt={beforeAlt} style={{ objectPosition }} draggable={false} loading="lazy" decoding="async" />

        <span className={`ff-ba__tag ff-ba__tag--before ${pos < 18 ? "is-hidden" : ""}`}>{beforeLabel}</span>
        <span className={`ff-ba__tag ff-ba__tag--after ${pos > 82 ? "is-hidden" : ""}`}>{afterLabel}</span>

        <input
          className="ff-ba__range"
          type="range"
          min="0"
          max="100"
          step="1"
          value={rounded}
          onChange={(e) => { engage(); apply(Number(e.target.value)); }}
          aria-label={`Vergleichsregler ${beforeLabel} / ${afterLabel}`}
          aria-valuetext={`${rounded} Prozent ${beforeLabel} sichtbar`}
        />
        <div className="ff-ba__handle" aria-hidden="true">
          <span className="ff-ba__grip">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M8.5 6.5 3 12l5.5 5.5 1.4-1.4L5.8 12l4.1-4.1zM15.5 6.5l-1.4 1.4 4.1 4.1-4.1 4.1 1.4 1.4L21 12z" />
            </svg>
          </span>
        </div>

        {hint && <span className="ff-ba__hint" aria-hidden="true">Regler ziehen</span>}
      </div>

      <div className="ff-ba__buttons">
        <button type="button" className={`ff-ba__btn ${pos > 92 ? "is-active" : ""}`} onClick={() => go(100)}>{beforeLabel} zeigen</button>
        <button type="button" className={`ff-ba__btn ${pos < 8 ? "is-active" : ""}`} onClick={() => go(0)}>{afterLabel} zeigen</button>
      </div>
      {caption && <figcaption className="ff-ba__caption">{caption}</figcaption>}
    </figure>
  );
}
